import { useEffect, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import type { UnifiedConversation, ConversationType } from "@/types/conversations";

const STALE = 60_000;

// ─── DM conversations ─────────────────────────────────
async function fetchDMConversations(userId: string): Promise<UnifiedConversation[]> {
  const { data: msgs } = await supabase
    .from("direct_messages")
    .select("id, sender_id, recipient_id, message, read_at, created_at, deleted_by")
    .or(`sender_id.eq.${userId},recipient_id.eq.${userId}`)
    .not("deleted_by", "cs", `{${userId}}`)
    .order("created_at", { ascending: false })
    .limit(500);

  const partnerMap = new Map<string, { lastMsg: any; unread: number }>();
  for (const m of msgs ?? []) {
    const pid = m.sender_id === userId ? m.recipient_id : m.sender_id;
    if (!partnerMap.has(pid)) partnerMap.set(pid, { lastMsg: m, unread: 0 });
    if (m.recipient_id === userId && !m.read_at) partnerMap.get(pid)!.unread++;
  }

  const ids = Array.from(partnerMap.keys());
  if (ids.length === 0) return [];

  const { data: profiles } = await supabase.from("public_profiles" as any).select("id, full_name, avatar_url").in("id", ids);
  const pMap = new Map((profiles ?? []).map((p: any) => [p.id, p]));

  return ids.map((pid) => {
    const { lastMsg, unread } = partnerMap.get(pid)!;
    const p = pMap.get(pid);
    return {
      id: `dm:${pid}`,
      type: "direct" as const,
      title: p?.full_name || "",
      avatarUrl: p?.avatar_url,
      lastMessage: lastMsg.message,
      lastMessageAt: lastMsg.created_at,
      lastMessageIsOwn: lastMsg.sender_id === userId,
      unreadCount: unread,
      meta: { type: "direct" as const, recipientId: pid },
    };
  });
}

// ─── Group chat conversations ──────────────────────────
async function fetchGroupConversations(userId: string): Promise<UnifiedConversation[]> {
  // Find groups where user is member, parent, or coach
  const { data: memberRows } = await supabase
    .from("group_members")
    .select("group_id")
    .or(`player_id.eq.${userId},parent_id.eq.${userId}`)
    .eq("status", "active");

  const { data: coachRows } = await supabase
    .from("training_groups")
    .select("id")
    .eq("coach_id", userId);

  const groupIds = [...new Set([
    ...(memberRows ?? []).map((r: any) => r.group_id),
    ...(coachRows ?? []).map((r: any) => r.id),
  ])];
  if (groupIds.length === 0) return [];

  // Fetch group info
  const { data: groups } = await supabase
    .from("training_groups")
    .select("id, name, color, coach_id, group_members(id)")
    .in("id", groupIds);

  // Fetch latest message per group (limit per group isn't possible, so limit total)
  const { data: latestMsgs } = await supabase
    .from("group_chat_messages")
    .select("group_id, message, sender_id, created_at, is_system")
    .in("group_id", groupIds)
    .order("created_at", { ascending: false })
    .limit(200);

  // Fetch read states
  const { data: readStates } = await supabase
    .from("message_read_state" as any)
    .select("source_id, last_read_at")
    .eq("user_id", userId)
    .eq("conversation_type", "group_chat")
    .in("source_id", groupIds);
  const readMap = new Map((readStates ?? []).map((r: any) => [r.source_id, r.last_read_at]));

  // Group latest non-system messages
  const lastMsgMap = new Map<string, any>();
  for (const m of latestMsgs ?? []) {
    if (!lastMsgMap.has(m.group_id) && !m.is_system) lastMsgMap.set(m.group_id, m);
  }

  return (groups ?? []).map((g: any) => {
    const lastMsg = lastMsgMap.get(g.id);
    const lastRead = readMap.get(g.id);
    // Count unread: messages after last_read_at
    const unread = lastRead && lastMsg
      ? (latestMsgs ?? []).filter((m: any) => m.group_id === g.id && m.created_at > lastRead && m.sender_id !== userId).length
      : lastMsg ? 1 : 0; // If never read but has messages, show 1

    return {
      id: `group:${g.id}`,
      type: "group_chat" as const,
      title: g.name,
      color: g.color,
      lastMessage: lastMsg?.message ?? "",
      lastMessageAt: lastMsg?.created_at ?? g.created_at ?? "",
      lastMessageIsOwn: lastMsg?.sender_id === userId,
      unreadCount: unread,
      meta: {
        type: "group_chat" as const,
        groupId: g.id,
        groupName: g.name,
        memberCount: g.group_members?.length,
        isCoachOrAdmin: g.coach_id === userId,
      },
    };
  }).filter((c: UnifiedConversation) => c.lastMessage); // Only show groups with messages
}

// ─── Open match chat conversations ─────────────────────
// Spår 1b: V2-chat (open_match_v2 + match_chat_messages.open_match_v2_id) raderas
// med V2-tabellerna. Hooken returnerar tom array tills V1-chat-koncept ev. byggs
// i Spår 3 (PadelMates-wizard). 'open_match_chat' UnifiedConversation-typ
// behålls i types för bakåtkompat (kallare som filtrerar på typ).
async function fetchOpenMatchConversations(_userId: string): Promise<UnifiedConversation[]> {
  return [];
}

// ─── Training conversations ────────────────────────────
async function fetchTrainingConversations(userId: string): Promise<UnifiedConversation[]> {
  const { data: msgs } = await supabase
    .from("training_messages" as any)
    .select("id, group_id, sender_id, recipient_id, player_id, message, read_at, created_at")
    .or(`sender_id.eq.${userId},recipient_id.eq.${userId}`)
    .order("created_at", { ascending: false })
    .limit(500);

  if (!msgs?.length) return [];

  // Group by (group_id, player_id) — each training thread
  const threadMap = new Map<string, { lastMsg: any; unread: number; partnerId: string }>();
  for (const m of msgs as any[]) {
    const key = `${m.group_id}:${m.player_id}`;
    const partnerId = m.sender_id === userId ? m.recipient_id : m.sender_id;
    if (!threadMap.has(key)) threadMap.set(key, { lastMsg: m, unread: 0, partnerId });
    if (m.recipient_id === userId && !m.read_at) threadMap.get(key)!.unread++;
  }

  // Fetch profiles + groups for display
  const allIds = [...new Set(Array.from(threadMap.values()).map((t) => t.partnerId))];
  const groupIds = [...new Set((msgs as any[]).map((m: any) => m.group_id))];

  const { data: profiles } = allIds.length > 0
    ? await supabase.from("public_profiles" as any).select("id, full_name, avatar_url").in("id", allIds)
    : { data: [] as { id: string; full_name: string; avatar_url: string }[] };
  const { data: groups } = groupIds.length > 0
    ? await supabase.from("training_groups").select("id, name, coach_id").in("id", groupIds)
    : { data: [] as { id: string; name: string; coach_id: string }[] };

  const profileMap = new Map((profiles ?? []).map((p: any) => [p.id, p]));
  const groupMap = new Map((groups ?? []).map((g: any) => [g.id, g]));

  return Array.from(threadMap.entries()).map(([key, { lastMsg, unread, partnerId }]) => {
    const profile = profileMap.get(partnerId);
    const group = groupMap.get(lastMsg.group_id);
    return {
      id: `training:${key}`,
      type: "training" as const,
      title: profile?.full_name || "",
      avatarUrl: profile?.avatar_url,
      lastMessage: lastMsg.message,
      lastMessageAt: lastMsg.created_at,
      lastMessageIsOwn: lastMsg.sender_id === userId,
      unreadCount: unread,
      meta: {
        type: "training" as const,
        groupId: lastMsg.group_id,
        coachId: group?.coach_id ?? partnerId,
        childId: lastMsg.player_id,
      },
    };
  });
}

// ─── Main hook ─────────────────────────────────────────
export function useUnifiedConversations(filter?: ConversationType, search?: string) {
  const { user } = useAuth();
  const qc = useQueryClient();

  const dm = useQuery({ queryKey: ["unified-dm", user?.id], queryFn: () => fetchDMConversations(user!.id), enabled: !!user, staleTime: STALE });
  const group = useQuery({ queryKey: ["unified-group", user?.id], queryFn: () => fetchGroupConversations(user!.id), enabled: !!user, staleTime: STALE });
  const openMatch = useQuery({ queryKey: ["unified-openmatch", user?.id], queryFn: () => fetchOpenMatchConversations(user!.id), enabled: !!user, staleTime: STALE });
  const training = useQuery({ queryKey: ["unified-training", user?.id], queryFn: () => fetchTrainingConversations(user!.id), enabled: !!user, staleTime: STALE });

  // Realtime subscriptions
  useEffect(() => {
    if (!user) return;
    const channels = [
      supabase.channel("unified-rt-dm")
        .on("postgres_changes", { event: "INSERT", schema: "public", table: "direct_messages" }, () => qc.invalidateQueries({ queryKey: ["unified-dm"] }))
        .on("postgres_changes", { event: "UPDATE", schema: "public", table: "direct_messages" }, () => qc.invalidateQueries({ queryKey: ["unified-dm"] })),
      supabase.channel("unified-rt-group").on("postgres_changes", { event: "INSERT", schema: "public", table: "group_chat_messages" }, () => qc.invalidateQueries({ queryKey: ["unified-group"] })),
      supabase.channel("unified-rt-match").on("postgres_changes", { event: "INSERT", schema: "public", table: "match_chat_messages" }, () => qc.invalidateQueries({ queryKey: ["unified-openmatch"] })),
      supabase.channel("unified-rt-training")
        .on("postgres_changes", { event: "INSERT", schema: "public", table: "training_messages" }, () => qc.invalidateQueries({ queryKey: ["unified-training"] }))
        .on("postgres_changes", { event: "UPDATE", schema: "public", table: "training_messages" }, () => qc.invalidateQueries({ queryKey: ["unified-training"] })),
    ];
    channels.forEach((c) => c.subscribe());
    return () => { channels.forEach((c) => supabase.removeChannel(c)); };
  }, [user, qc]);

  const conversations = useMemo(() => {
    const all = [...(dm.data ?? []), ...(group.data ?? []), ...(openMatch.data ?? []), ...(training.data ?? [])];
    let filtered = filter ? all.filter((c) => c.type === filter) : all;
    if (search) {
      const q = search.toLowerCase();
      filtered = filtered.filter((c) => c.title.toLowerCase().includes(q) || c.lastMessage.toLowerCase().includes(q));
    }
    return filtered.sort((a, b) => new Date(b.lastMessageAt).getTime() - new Date(a.lastMessageAt).getTime());
  }, [dm.data, group.data, openMatch.data, training.data, filter, search]);

  const unreadByType = useMemo(() => ({
    total: conversations.reduce((s, c) => s + c.unreadCount, 0),
    direct: (dm.data ?? []).reduce((s, c) => s + c.unreadCount, 0),
    group_chat: (group.data ?? []).reduce((s, c) => s + c.unreadCount, 0),
    open_match_chat: (openMatch.data ?? []).reduce((s, c) => s + c.unreadCount, 0),
    training: (training.data ?? []).reduce((s, c) => s + c.unreadCount, 0),
  }), [conversations, dm.data, group.data, openMatch.data, training.data]);

  return { conversations, isLoading: dm.isLoading || group.isLoading || openMatch.isLoading || training.isLoading, unreadByType };
}
